import { useEffect, useState } from "react";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import {
  formatDate,
  formatPercentage,
  change3Digits,
} from "../../utils/format";
import {
  Table as CSDTable,
  Box,
  Header,
  SpaceBetween,
  Spinner,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { Link } from "react-router-dom";
import { COMMON_LABELS } from "../default-constants";
import { LABELS } from "./constants";
import { useAppSelector } from "../../app/hook";
import type { RootState } from "../../app/store";
import {
  useGetRateAdjustmentCasesQuery,
  useLazyGetRateAdjustmentCaseDownloadQuery,
} from "../../features/api";
import { IconButton } from "../UI/button/IconButton";

const columnDefinitions = [
  {
    id: "corporateNumber",
    header: LABELS.CORPORATE_NUMBER,
    cell: (e: any) => (
      // 料率調整案件更新画面遷移
      <Link to={"/rate-adjustment-case/" + e.rateAdjustmentCaseNo}>
        {e.corporateNumber}
      </Link>
    ),
  },
  {
    id: "hasContract",
    header: LABELS.CONTRACT_PRESENCE_ABSENCE,
    cell: (e: any) => e.hasContract,
  },
  {
    id: "companyName",
    header: LABELS.COMPANY_NAME,
    cell: (e: any) => e.companyName,
  },
  {
    id: "merchantName",
    header: LABELS.MERCHANT_NAME,
    cell: (e: any) => e.merchantName,
  },
  {
    id: "responseRate",
    header: LABELS.RESPONSE_RATE,
    cell: (e: any) => formatPercentage(e.responseRate),
  },
  {
    id: "contractRouteName",
    header: LABELS.CONTRACT_ROUTE,
    cell: (e: any) => e.contractRouteName,
  },
  {
    id: "applicationDate",
    header: LABELS.APPLICATION_DATE,
    cell: (e: any) => formatDate(e.applicationDate),
  },
  {
    id: "responseDate",
    header: LABELS.RESPONSE_DATE,
    cell: (e: any) => formatDate(e.responseDate),
  },
  {
    id: "serviceName",
    header: LABELS.SERVICE_NAME,
    cell: (e: any) => e.serviceName,
  },
  {
    id: "docomoIndustry",
    header: LABELS.DOCOMO_INDUSTRY,
    cell: (e: any) => e.docomoIndustry,
  },
  {
    id: "applicationIndustry",
    header: LABELS.APPLICATION_INDUSTRY,
    cell: (e: any) => e.applicationIndustry,
  },
  {
    id: "sales",
    header: LABELS.LIST_SALES,
    cell: (e: any) => change3Digits(e.sales),
  },
  {
    id: "allShopsNumber",
    header: LABELS.ALL_SHOP_NUMBER,
    cell: (e: any) => e.allShopsNumber,
  },
  {
    id: "installedShopsNumber",
    header: LABELS.INSTALLED_SHOP_NUMBER,
    cell: (e: any) => e.installedShopsNumber,
  },
  {
    id: "ratePresentationCriteria",
    header: LABELS.RATE_PRESENTATION_CONDITION,
    cell: (e: any) => e.ratePresentationCriteria,
  },
  {
    id: "desiredRate",
    header: LABELS.DESIRED_RATE,
    cell: (e: any) => e.desiredRate,
  },
  {
    id: "costBurden",
    header: LABELS.COST_BURDEN,
    cell: (e: any) => e.costBurden,
  },
  {
    id: "costBurdenAdjustmentInformation",
    header: LABELS.COST_BURDEN_ADJUSTMENT_INFO,
    cell: (e: any) => e.costBurdenAdjustmentInformation,
  },
  {
    id: "note",
    header: LABELS.NOTE,
    cell: (e: any) => e.note,
  },
];

/**
 * 料率調整案件一覧
 */
const Table = () => {
  const searchCondition = useAppSelector(
    (state: RootState) => state.rateAdjustmentCaseSearchCondition
  );

  //一覧データの取得
  const { data, isFetching, refetch } =
    useGetRateAdjustmentCasesQuery(searchCondition);

  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);
  useEffect(() => {
    if (data !== undefined) {
      setPagedItems(getPagedItems(data, 1, preferences.pageSize));
      setAllItems(data);
      setCurrentPageIndex(1);
    }
  }, [data, preferences.pageSize]);

  // spinner・文字列表示有無
  const [isDownloadSpinnerAndStrDisplay, setIsDownloadSpinnerAndStrDisplay] = useState<boolean>(false);

  // button表示有無
  const [isRateAdjustmentCaseDownloadButton, setIsRateAdjustmentCaseDownloadButton] = useState<boolean>(false);

  // ダウンロード関連変数
  const [lazyDownloadRateAdjustmentCaseTrigger] = useLazyGetRateAdjustmentCaseDownloadQuery();

  useEffect(() => {
    refetch();
  }, [refetch]);

  //DLボタン押下イベント関数
  const onClickDownloadButton = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    executeDownload();
  };

  const executeDownload = async () => {
    // spinner・文字列活性
    setIsDownloadSpinnerAndStrDisplay(true);
    // button活性
    setIsRateAdjustmentCaseDownloadButton(true);
    await lazyDownloadRateAdjustmentCaseTrigger(searchCondition)
      .unwrap()
      .then()
      .finally(() => {
        // spinner・文字列非活性
        setIsDownloadSpinnerAndStrDisplay(false);
        // button非活性
        setIsRateAdjustmentCaseDownloadButton(false);
      });
  };

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    if (data !== undefined) {
      setPagedItems(data.slice(0, newPageSize));
      setCurrentPageIndex(1);
    }
  };

  return (
    <div>
      <SpaceBetween size="m">
        <CSDTable
          columnDefinitions={columnDefinitions}
          loading={isFetching}
          loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
          items={pagedItems}
          resizableColumns
          stickyHeader
          header={
            <Header
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {isDownloadSpinnerAndStrDisplay ? <Spinner size="big" /> : <></>} 
                  {isDownloadSpinnerAndStrDisplay ? <>{COMMON_LABELS.CONTENTS.DL_RUN}</> : <></>}
                  <IconButton
                    iconName={"download"}
                    label={COMMON_LABELS.BTNS.DL}
                    disabled={isRateAdjustmentCaseDownloadButton}
                    onClick={onClickDownloadButton}
                  />
                </SpaceBetween>
              }
            >
              {LABELS.TITLE}
              {getHeaderCounterText(allItems.length, pagedItems.length)}
            </Header>
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              filteredItems={allItems}
              setCurrentPageIndex={setCurrentPageIndex}
              pageSize={preferences.pageSize}
              setPagedItems={setPagedItems}
            />
          }
          preferences={
            <Preference
              preferences={preferences}
              options={options}
              handleOnConfirm={handleOnConfirm}
            />
          }
          empty={
            <Box textAlign="center" color="inherit">
              {COMMON_LABELS.CSD.NO_DATA}
            </Box>
          }
        />
      </SpaceBetween>
    </div>
  );
};

export default Table;
